import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "./Steps.css";
import ZilchTooltip from "../ZilchToolTip/ZilchTooltip";
import { useSupplierLocations, countryOptionsWith, OTHER_COUNTRY } from "../../utils/useSupplierLocations";

function Step2MaterialAcquisition({
  formData,
  materialOptions,
  countries,
  parentCategories = [],
  decorationOptions = [],
  decorationOptionsLoading = false,
  decorationOptionsError = "",
  updateFormData,
  updateNestedFormData,
  updateArrayData,
  addPart,
  removePart,
  addBrandingProcess,
  removeBrandingProcess,
  updateBrandingProcessField,
  validationErrors = [],
  aiPrefill = null,
  fetchAiPrefill,
  aiLoading = false,
  aiError = null,
}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
  const [subcategories, setSubcategories] = useState([]);
  const [finishedProducts, setFinishedProducts] = useState([]);
  const lastFetchedVariant = useRef(null);
  
  // Material states for parts-based assessment
  const [materialCategories, setMaterialCategories] = useState([]);
  const [materialSubcategoriesCache, setMaterialSubcategoriesCache] = useState({});
  const [materialQualitiesCache, setMaterialQualitiesCache] = useState({});
  const [processCategoriesCache, setProcessCategoriesCache] = useState({});
  const [processesCache, setProcessesCache] = useState({});

  // Country-of-origin options (countries with an uplift factor, from the DB)
  const supplierCountries = useSupplierLocations();

  // Material emissions for one part.
  //
  // Mirrors calculateMaterialEmissions in server/emissionsCalculator.js — the
  // server recomputes from the saved parts JSONB, so the two must agree. Most
  // materials are per kg; per-item materials bill on quantity instead. The
  // process EF is always per kg, so it stays on weight either way.
  const partEmissions = (part, overrides = {}) => {
    const merged = { ...part, ...overrides };
    const materialEf = Number(merged.ef_kgco2_per_kg) || 0;
    const processEf = Number(merged.process_ef_kgco2_per_kg) || 0;
    const weightKg = (Number(merged.weight_grams) || 0) / 1000;

    // Per-item parts aren't weighed, so there's no mass for a per-kg process
    // EF to act on — quantity × material EF is the whole footprint.
    if (merged.material_unit === 'per_item') {
      return (Number(merged.quantity) || 0) * materialEf;
    }
    return weightKg * (materialEf + processEf);
  };

  // Write a recalculated emissions_kg back onto a part.
  const recalcPart = (partIndex, part, overrides = {}) => {
    updateArrayData("materialAcquisition", "parts", partIndex, "emissions_kg", partEmissions(part, overrides));
  };

  // The unit ('per_kg' | 'per_item') of a part's selected material, read from
  // the cached quality rows. Falls back to whatever the part already carries so
  // saved assessments render correctly before their options finish loading.
  const unitForPart = (part) => {
    const qualities = materialQualitiesCache[`${part.material_category}::${part.material_subcategory}`] || [];
    const row = qualities.find((q) => q.quality === part.material_quality);
    if (row?.unit) return row.unit;

    // No quality picked yet. If every quality of this material is per-item then
    // the unit is already settled, so the form can drop the weight field as
    // soon as the material is chosen rather than waiting on the quality.
    if (qualities.length > 0 && qualities.every((q) => q.unit === 'per_item')) return 'per_item';

    return part.material_unit || 'per_kg';
  };

  // Helper to check if a field has an error
  const hasError = (fieldKeyword) => {
    return validationErrors.some(err => err.toLowerCase().includes(fieldKeyword.toLowerCase()));
  };

  // Helper to get part-specific errors
  const getPartError = (partIndex, field) => {
    return validationErrors.find(err => 
      err.toLowerCase().includes(`part ${partIndex + 1}`) && 
      err.toLowerCase().includes(field.toLowerCase())
    );
  };

  

  const parentSlug = formData.materialAcquisition.finishedProduct.parentCategorySlug || '';
  const subcatSlug = formData.materialAcquisition.finishedProduct.subcategorySlug || formData.materialAcquisition.finishedProduct.category || '';
  const selectedProductSlug = formData.materialAcquisition.finishedProduct.productSlug || '';
  const selectedQuality = formData.materialAcquisition.finishedProduct.quality || '';

  // Load subcategories when parent changes
  useEffect(() => {
    if (!parentSlug) { setSubcategories([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/taxonomy/${parentSlug}/subcategories`);
        setSubcategories(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        setSubcategories([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentSlug]);

  // Load finished products when subcategory changes
  useEffect(() => {
    if (!subcatSlug) { setFinishedProducts([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/taxonomy/subcategories/${subcatSlug}/finished-products`);
        setFinishedProducts(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        setFinishedProducts([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcatSlug]);

  // Auto-select "virgin/average" quality when a product is selected
  useEffect(() => {
    if (!selectedProductSlug || finishedProducts.length === 0) return;
    // Only auto-select if quality isn't already set
    if (selectedQuality) return;
    const qualities = [...new Set(finishedProducts.filter((fp) => fp.slug === selectedProductSlug).map((fp) => fp.quality))];
    const preferred = qualities.find((q) => /virgin.*average|average.*virgin/i.test(q)) ||
      qualities.find((q) => /virgin/i.test(q)) ||
      qualities.find((q) => /average/i.test(q));
    if (preferred) {
      updateNestedFormData('materialAcquisition', 'finishedProduct', 'quality', preferred);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductSlug, finishedProducts]);

  // When product or quality changes, fetch the variant to fill EF/composition
  useEffect(() => {
    (async () => {
      if (!subcatSlug || !selectedProductSlug || !selectedQuality) return;
      
      // Create a unique key for this variant to avoid re-fetching the same data
      const variantKey = `${subcatSlug}-${selectedProductSlug}-${selectedQuality}`;
      
      // Skip if we've already fetched this exact variant
      if (lastFetchedVariant.current === variantKey) return;
      
      try {
        const { data } = await axios.get(`${API_BASE_URL}/taxonomy/subcategories/${subcatSlug}/finished-products/${selectedProductSlug}`, { params: { quality: selectedQuality } });
        const v = data?.data;
        if (v) {
          lastFetchedVariant.current = variantKey;
          updateNestedFormData('materialAcquisition', 'finishedProduct', 'industryAverage', v.ef_kgco2_per_kg);
          updateNestedFormData('materialAcquisition', 'finishedProduct', 'composition', v.composition || []);
          updateNestedFormData('materialAcquisition', 'finishedProduct', 'processes', v.processes || []);
        }
      } catch (e) {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcatSlug, selectedProductSlug, selectedQuality]);

  // Pre-populate caches for any parts already loaded (saved assessments or AI suggestions)
  useEffect(() => {
    formData.materialAcquisition.parts.forEach((part) => {
      if (part.material_category) {
        // Ensure subcategory dropdown options are loaded
        if (!materialSubcategoriesCache[part.material_category]) {
          loadMaterialSubcategories(part.material_category);
        }
        if (part.material_subcategory) {
          // Ensure quality dropdown options are loaded
          const qualKey = `${part.material_category}::${part.material_subcategory}`;
          if (!materialQualitiesCache[qualKey]) {
            loadMaterialQualities(part.material_category, part.material_subcategory);
          }
          // Ensure process category options are loaded
          if (!processCategoriesCache[qualKey]) {
            loadProcessCategories(part.material_category, part.material_subcategory);
          }
          if (part.process_category_id) {
            const procKey = `${qualKey}::${part.process_category_id}`;
            if (!processesCache[procKey]) {
              loadProcesses(part.material_category, part.material_subcategory, part.process_category_id);
            }
          }
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.materialAcquisition.parts]);

  // Most materials (122 of 143 subcategories) publish a single quality, so the
  // dropdown offers exactly one choice. Pick it automatically — it costs the
  // user a click for no decision, and until it's set the part has no emission
  // factor and no resolved unit. The field stays visible so the assessment
  // still shows which quality was used.
  const autoSelectedQualities = useRef(new Set());
  useEffect(() => {
    formData.materialAcquisition.parts.forEach((part, index) => {
      if (!part.material_category || !part.material_subcategory || part.material_quality) return;

      const qualities = materialQualitiesCache[`${part.material_category}::${part.material_subcategory}`] || [];
      if (qualities.length !== 1) return;

      // Guard against re-firing while the EF fetch is still in flight.
      const key = `${index}::${part.material_category}::${part.material_subcategory}`;
      if (autoSelectedQualities.current.has(key)) return;
      autoSelectedQualities.current.add(key);

      handlePartQualityChange(index, qualities[0].quality);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.materialAcquisition.parts, materialQualitiesCache]);

  // Reconcile each part's material_unit against the real unit from the DB once
  // the quality options have loaded. Saved assessments predating the per-item
  // column, and AI-prefilled parts, arrive without a unit — without this they
  // would bill a per-item material on weight.
  useEffect(() => {
    formData.materialAcquisition.parts.forEach((part, index) => {
      if (!part.material_quality) return;
      const actualUnit = unitForPart(part);
      if (actualUnit === part.material_unit) return;

      updateArrayData("materialAcquisition", "parts", index, "material_unit", actualUnit);
      const quantity = actualUnit === 'per_item' ? (Number(part.quantity) || 1) : part.quantity;
      const weight = actualUnit === 'per_item' ? "" : part.weight_grams;
      if (actualUnit === 'per_item') {
        updateArrayData("materialAcquisition", "parts", index, "quantity", quantity);
        updateArrayData("materialAcquisition", "parts", index, "weight_grams", "");
      }
      recalcPart(index, part, { material_unit: actualUnit, quantity, weight_grams: weight });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.materialAcquisition.parts, materialQualitiesCache]);

  // Load material categories on component mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/materials/categories`);
        setMaterialCategories(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        console.error('Error loading material categories:', e);
        setMaterialCategories([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Normalize a prefilled / AI-returned country to the canonical dropdown value
  // (e.g. "china" / "China " → "China") so the <select> shows it as selected.
  // Values that don't match a factor country are left intact and preserved as
  // their own option in the render below.
  useEffect(() => {
    const prefilled = formData.materialAcquisition.supplierLocation;
    if (!prefilled || supplierCountries.length === 0) return;

    const normalizedPrefilled = String(prefilled).trim().toLowerCase();
    const matched = supplierCountries.find(
      (country) => country.toLowerCase() === normalizedPrefilled
    );

    if (matched && matched !== prefilled) {
      updateFormData("materialAcquisition", "supplierLocation", matched);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierCountries, formData.materialAcquisition.supplierLocation]);

  // Helper function to load material subcategories for a category
  const loadMaterialSubcategories = async (category) => {
    if (materialSubcategoriesCache[category]) {
      return materialSubcategoriesCache[category];
    }
    try {
      const { data } = await axios.get(`${API_BASE_URL}/materials/categories/${encodeURIComponent(category)}/subcategories`);
      const subcats = Array.isArray(data?.data) ? data.data : [];
      setMaterialSubcategoriesCache(prev => ({ ...prev, [category]: subcats }));
      return subcats;
    } catch (e) {
      console.error('Error loading material subcategories:', e);
      return [];
    }
  };

  // Helper function to load available qualities for a material
  const loadMaterialQualities = async (category, subcategory) => {
    const cacheKey = `${category}::${subcategory}`;
    if (materialQualitiesCache[cacheKey]) {
      return materialQualitiesCache[cacheKey];
    }
    try {
      const { data } = await axios.get(`${API_BASE_URL}/materials/emission-factor`, {
        params: { category, subcategory }
      });
      const qualities = Array.isArray(data?.data) ? data.data : [];
      setMaterialQualitiesCache(prev => ({ ...prev, [cacheKey]: qualities }));
      return qualities;
    } catch (e) {
      console.error('Error loading material qualities:', e);
      return [];
    }
  };

  // Load process categories for a material (category + subcategory)
  const loadProcessCategories = async (category, subcategory) => {
    const cacheKey = `${category}::${subcategory}`;
    if (processCategoriesCache[cacheKey]) return processCategoriesCache[cacheKey];
    try {
      const { data } = await axios.get(`${API_BASE_URL}/materials/process-categories`, {
        params: { category, subcategory }
      });
      const cats = Array.isArray(data?.data) ? data.data : [];
      setProcessCategoriesCache(prev => ({ ...prev, [cacheKey]: cats }));
      return cats;
    } catch (e) {
      console.error('Error loading process categories:', e);
      return [];
    }
  };

  // Load processes for a material + process category
  const loadProcesses = async (category, subcategory, processCategoryId) => {
    const cacheKey = `${category}::${subcategory}::${processCategoryId}`;
    if (processesCache[cacheKey]) return processesCache[cacheKey];
    try {
      const { data } = await axios.get(`${API_BASE_URL}/materials/processes`, {
        params: { category, subcategory, process_category_id: processCategoryId }
      });
      const procs = Array.isArray(data?.data) ? data.data : [];
      setProcessesCache(prev => ({ ...prev, [cacheKey]: procs }));
      return procs;
    } catch (e) {
      console.error('Error loading processes:', e);
      return [];
    }
  };

  // Helper function to handle material category change for a part
  const handlePartCategoryChange = async (partIndex, category) => {
    updateArrayData("materialAcquisition", "parts", partIndex, "material_category", category);
    updateArrayData("materialAcquisition", "parts", partIndex, "material_subcategory", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "material_quality", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "ef_kgco2_per_kg", null);
    updateArrayData("materialAcquisition", "parts", partIndex, "process_category_id", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "process_id", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "process_name", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "process_ef_kgco2_per_kg", null);
    updateArrayData("materialAcquisition", "parts", partIndex, "emissions_kg", null);

    if (category) {
      await loadMaterialSubcategories(category);
    }
  };

  // Helper function to handle material subcategory change for a part
  const handlePartSubcategoryChange = async (partIndex, subcategory) => {
    const part = formData.materialAcquisition.parts[partIndex];
    updateArrayData("materialAcquisition", "parts", partIndex, "material_subcategory", subcategory);
    updateArrayData("materialAcquisition", "parts", partIndex, "material_quality", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "ef_kgco2_per_kg", null);
    updateArrayData("materialAcquisition", "parts", partIndex, "process_category_id", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "process_id", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "process_name", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "process_ef_kgco2_per_kg", null);
    updateArrayData("materialAcquisition", "parts", partIndex, "emissions_kg", null);

    if (subcategory && part.material_category) {
      await loadMaterialQualities(part.material_category, subcategory);
      await loadProcessCategories(part.material_category, subcategory);
    }
  };

  // Helper function to handle material quality change for a part
  const handlePartQualityChange = async (partIndex, quality) => {
    const part = formData.materialAcquisition.parts[partIndex];
    updateArrayData("materialAcquisition", "parts", partIndex, "material_quality", quality);

    if (quality && part.material_category && part.material_subcategory) {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/materials/emission-factor`, {
          params: {
            category: part.material_category,
            subcategory: part.material_subcategory,
            quality: quality
          }
        });
        const ef = data?.data?.ef_kgco2_per_kg;
        const unit = data?.data?.unit || 'per_kg';
        if (ef) {
          updateArrayData("materialAcquisition", "parts", partIndex, "ef_kgco2_per_kg", ef);
          updateArrayData("materialAcquisition", "parts", partIndex, "material_unit", unit);
          // A per-item material needs a quantity; default to 1 rather than
          // leaving the new field empty and failing validation. Any weight
          // typed before this material was picked is cleared — the field is
          // gone from the form, so leaving the value on the part would keep it
          // counting toward totals invisibly.
          const quantity = unit === 'per_item' ? (Number(part.quantity) || 1) : part.quantity;
          const weight = unit === 'per_item' ? "" : part.weight_grams;
          if (unit === 'per_item') {
            updateArrayData("materialAcquisition", "parts", partIndex, "quantity", quantity);
            updateArrayData("materialAcquisition", "parts", partIndex, "weight_grams", "");
          }
          recalcPart(partIndex, part, {
            ef_kgco2_per_kg: ef,
            material_unit: unit,
            quantity,
            weight_grams: weight,
          });
        }
      } catch (e) {
        console.error('Error fetching emission factor:', e);
      }
    }
  };

  // Handle process category selection for a part
  const handlePartProcessCategoryChange = async (partIndex, processCategoryId) => {
    const part = formData.materialAcquisition.parts[partIndex];
    updateArrayData("materialAcquisition", "parts", partIndex, "process_category_id", processCategoryId);
    updateArrayData("materialAcquisition", "parts", partIndex, "process_id", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "process_name", "");
    updateArrayData("materialAcquisition", "parts", partIndex, "process_ef_kgco2_per_kg", null);

    // Recalculate emissions with no process EF (process cleared)
    recalcPart(partIndex, part, { process_ef_kgco2_per_kg: null });

    if (processCategoryId && part.material_category && part.material_subcategory) {
      await loadProcesses(part.material_category, part.material_subcategory, processCategoryId);
    }
  };

  // Handle process selection for a part
  const handlePartProcessChange = (partIndex, processId) => {
    const part = formData.materialAcquisition.parts[partIndex];
    const processCacheKey = `${part.material_category}::${part.material_subcategory}::${part.process_category_id}`;
    const availableProcesses = processesCache[processCacheKey] || [];
    const selectedProcess = availableProcesses.find(p => p.id === processId);

    updateArrayData("materialAcquisition", "parts", partIndex, "process_id", processId);
    updateArrayData("materialAcquisition", "parts", partIndex, "process_name", selectedProcess?.name || "");
    updateArrayData("materialAcquisition", "parts", partIndex, "process_ef_kgco2_per_kg", selectedProcess?.ef_kgco2_per_kg || null);

    // Recalculate emissions including process EF
    recalcPart(partIndex, part, { process_ef_kgco2_per_kg: selectedProcess?.ef_kgco2_per_kg || null });
  };

  // Helper function to calculate total parts weight.
  // Per-item parts are never weighed, so they contribute nothing here even if
  // a weight lingers on them from before their material was chosen.
  const calculateTotalPartsWeight = () => {
    return formData.materialAcquisition.parts.reduce((sum, part) => {
      if (unitForPart(part) === 'per_item') return sum;
      return sum + (Number(part.weight_grams) || 0);
    }, 0);
  };

  // Helper function to check if weight is within tolerance
  const isWeightWithinTolerance = () => {
    const productWeight = Number(formData.productInfo.weight) || 0;
    if (productWeight === 0) return true;
    
    const totalPartsWeight = calculateTotalPartsWeight();
    const lowerBound = productWeight * 0.95;
    const upperBound = productWeight * 1.05;
    
    return totalPartsWeight >= lowerBound && totalPartsWeight <= upperBound;
  };

  // Per-item parts carry real mass that the user is never asked to measure, so
  // the weighed parts can't be expected to reach the product weight. When one
  // is present the ±5% floor is lifted — but parts still can't outweigh the
  // finished product, so that stays worth flagging.
  const hasPerItemPart = () =>
    formData.materialAcquisition.parts.some((part) => unitForPart(part) === 'per_item');

  const isOverProductWeight = () => {
    const productWeight = Number(formData.productInfo.weight) || 0;
    if (productWeight === 0) return false;
    return calculateTotalPartsWeight() > productWeight;
  };

  // Helper function to format composition for display
  const formatComposition = (composition) => {
    if (!composition || composition.length === 0) return '';
    return composition
      .map(c => `${c.material}`)
      .join(', ');
  };

  // Helper function to get unique products grouped by slug
  const getUniqueProducts = () => {
    if (!finishedProducts || finishedProducts.length === 0) return [];
    const productMap = new Map();
    finishedProducts.forEach((fp) => {
      if (!productMap.has(fp.slug)) {
        productMap.set(fp.slug, fp);
      }
    });
    return Array.from(productMap.values());
  };

  // Helper to check if a field is filled
  const isFilled = (value) => {
    return value !== "" && value !== null && value !== undefined;
  };

  return (
    <div className="step-container">
      {/* Header */}
      <div className="step-header">
        <div className="step-number">02</div>
        <div>
          <h2 className="step-title">Material Acquisition</h2>
          <p className="step-subtitle">Choose how you want to calculate material emissions</p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="form-section">
        {/* Assessment Type */}
        <div className="form-field">
          <label className="field-label">
            Assessment Method
            <span className="field-required">*</span>
          </label>
          <div className="justify-content-between d-sm-block d-md-flex gap-1">
            <label className={`radio-card mb-sm-2 mb-xs-2 mb-lg-0 ${formData.materialAcquisition.assessmentType === "finished" ? "radio-card--selected" : ""}`}>
              <input
                type="radio"
                name="assessmentType"
                value="finished"
                checked={formData.materialAcquisition.assessmentType === "finished"}
                onChange={() => updateFormData("materialAcquisition", "assessmentType", "finished")}
                className="radio-input"
              />
              <div className="radio-content">
                <div className="radio-title">Finished Product (Quick)</div>
                <div className="radio-description">Use industry-standard data for complete products</div>
              </div>
              <div className="radio-check">
                {formData.materialAcquisition.assessmentType === "finished" && (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="#06a457"/>
                    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </label>

            <label className={`radio-card ${formData.materialAcquisition.assessmentType === "parts" ? "radio-card--selected" : ""}`}>
              <input
                type="radio"
                name="assessmentType"
                value="parts"
                checked={formData.materialAcquisition.assessmentType === "parts"}
                onChange={() => updateFormData("materialAcquisition", "assessmentType", "parts")}
                className="radio-input"
              />
              <div className="radio-content">
                <div className="radio-title">Parts-Based (Detailed)</div>
                <div className="radio-description">Calculate from individual material components</div>
              </div>
              <div className="radio-check">
                {formData.materialAcquisition.assessmentType === "parts" && (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="#06a457"/>
                    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </label>
          </div>
        </div>
        {/* Supplier Location - Show for all Purchased products (always needed for reports) */}
        {formData.productInfo.productType === "purchased" && (
          <div className="form-field">
            <label className="field-label">
              Country of Origin
              <ZilchTooltip copy={"The country where your product is made — this matches the product's Country of Origin. We use it to calculate the manufacturing emissions. (The precise ships-from address is set separately as the transport origin.)"} />
            </label>
            <select
              className={`field-input field-select ${isFilled(formData.materialAcquisition.supplierLocation) ? "field-input--filled" : ""}`}
              value={formData.materialAcquisition.supplierLocation || ""}
              onChange={(e) => updateFormData("materialAcquisition", "supplierLocation", e.target.value || null)}
            >
              <option value="">Select country of origin...</option>
              {countryOptionsWith(formData.materialAcquisition.supplierLocation, supplierCountries).map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
              <option value={OTHER_COUNTRY}>Other / not listed</option>
            </select>
          </div>
        )}

        {/* AI Suggest Materials - Parts-Based only */}
        {formData.materialAcquisition.assessmentType === "parts" && fetchAiPrefill && (
          <div className="mb-3">
            <div className="card border-0 bg-light p-3 d-flex flex-row align-items-center justify-content-between gap-3">
              <div>
                <p className="fw-semibold mb-0 small">✨ Let AI suggest your materials (beta)</p>
                <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                  We'll scan this product's source data and match materials to our emissions database for you to review.
                </p>
              </div>
              <button
                className="btn btn-primary btn-sm text-nowrap"
                onClick={fetchAiPrefill}
                disabled={aiLoading}
              >
                {aiLoading
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Scanning...</>
                  : aiError ? 'Try again' : 'Suggest materials'}
              </button>
            </div>
            {aiError && (
              <div className="alert alert-warning py-2 px-3 mb-0 mt-2 small" role="alert">
                <strong>AI unavailable:</strong> {aiError} You can still fill in materials manually below.
              </div>
            )}
          </div>
        )}

        {/* Finished Product Section */}
        {formData.materialAcquisition.assessmentType === "finished" && (
          <div className="conditional-section">
            <div className="form-row">
              <div className="form-field">
                <label className="field-label">
                  Parent Category
                  <span className="field-required">*</span>
                </label>
                <select
                  className={`field-input field-select ${hasError("parent category") ? "field-input--error" : ""} ${isFilled(parentSlug) ? "field-input--filled" : ""}`}
                  value={parentSlug}
                  onChange={(e) => {
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'parentCategorySlug', e.target.value);
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'subcategorySlug', '');
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'productSlug', '');
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'quality', '');
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'industryAverage', 0);
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'composition', null);
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'processes', null);
                  }}
                >
                  <option value="">Select parent category...</option>
                  {parentCategories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
                {hasError("parent category") && (
                  <div className="field-error">Parent category is required</div>
                )}
              </div>

              <div className="form-field">
                <label className="field-label">
                  Subcategory
                  <span className="field-required">*</span>
                </label>
                <select
                  className={`field-input field-select ${hasError("subcategory") ? "field-input--error" : ""} ${isFilled(subcatSlug) ? "field-input--filled" : ""}`}
                  value={subcatSlug}
                  onChange={(e) => {
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'subcategorySlug', e.target.value);
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'productSlug', '');
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'quality', '');
                  }}
                  disabled={!parentSlug}
                  style={!parentSlug ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                >
                  <option value="">Select subcategory...</option>
                  {subcategories.map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
                {hasError("subcategory") && (
                  <div className="field-error">Subcategory is required</div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="field-label">
                  Product
                  <span className="field-required">*</span>
                </label>
                <select 
                  className={`field-input field-select ${hasError("product from the subcategory") ? "field-input--error" : ""} ${isFilled(selectedProductSlug) ? "field-input--filled" : ""}`}
                  value={selectedProductSlug} 
                  onChange={(e) => {
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'productSlug', e.target.value);
                    updateNestedFormData('materialAcquisition', 'finishedProduct', 'quality', '');
                  }} 
                  disabled={!subcatSlug}
                  style={!subcatSlug ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                >
                  <option value="">Select product...</option>
                  {getUniqueProducts().map((fp) => (
                    <option key={fp.slug} value={fp.slug}>{fp.display_name}</option>
                  ))}
                </select>
                {hasError("product from the subcategory") && (
                  <div className="field-error">Product is required</div>
                )}
              </div>

              <div className="form-field">
                <label className="field-label">
                  Quality
                  <span className="field-required">*</span>
                </label>
                <select 
                  className={`field-input field-select ${hasError("quality") ? "field-input--error" : ""} ${isFilled(selectedQuality) ? "field-input--filled" : ""}`}
                  value={selectedQuality} 
                  onChange={(e) => updateNestedFormData('materialAcquisition', 'finishedProduct', 'quality', e.target.value)} 
                  disabled={!selectedProductSlug}
                  style={!selectedProductSlug ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                >
                  <option value="">Select quality...</option>
                  {[...new Set(finishedProducts.filter((fp) => fp.slug === selectedProductSlug).map((fp) => fp.quality))].map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
                {hasError("quality") && (
                  <div className="field-error">Quality is required</div>
                )}
              </div>
            </div>

            {/* Composition Display - requested to remove - might add back later*/}
             {selectedProductSlug && selectedQuality && formData.materialAcquisition.finishedProduct.composition && formData.materialAcquisition.finishedProduct.composition.length > 0 && (
              <div className="form-field" style={{ marginTop: '-8px' }}>
                <div style={{ 
                  fontSize: '12px', 
                  color: '#64748b', 
                  fontStyle: 'italic',
                  marginTop: '4px'
                }}>
                  Composition Estimate: {formatComposition(formData.materialAcquisition.finishedProduct.composition)}
                </div>
              </div>
            )} 
          </div>
        )}

        {/* Parts-Based Section */}
        {formData.materialAcquisition.assessmentType === "parts" && (
          <div className="conditional-section">
            <div className="section-header">
              <div>
                <h3 className="section-title">Material Parts</h3>
                <p className="section-description">Add each material component of your product</p>
              </div>
              
            </div>

            {aiPrefill && formData.materialAcquisition.parts.some(p => p.ai_suggested) && (
              <div className="alert alert-info d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: '0.875rem' }}>
                <span>✨</span>
                <span>Materials pre-filled by AI — check weights and adjust as needed. All fields are editable.</span>
              </div>
            )}

            {formData.materialAcquisition.parts.length === 0 && (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <rect x="8" y="8" width="32" height="32" rx="4" stroke="#cbd5e1" strokeWidth="2"/>
                  <path d="M24 18V30M18 24H30" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <p className="empty-state-text">No parts added yet. Click "Add Part" to begin.</p>
              </div>
            )}
            

            {formData.materialAcquisition.parts.map((part, index) => {
              const categorySubcategories = materialSubcategoriesCache[part.material_category] || [];
              const cacheKey = `${part.material_category}::${part.material_subcategory}`;
              const availableQualities = materialQualitiesCache[cacheKey] || [];

              return (
                <div key={index} className="item-card">
                  <div className="item-card-header">
                    <div className="d-flex align-items-center gap-2">
                      <span className="item-badge">Part {index + 1}</span>
                      {part.ai_suggested && (
                        <span className="badge text-bg-info" style={{ fontSize: '0.7rem', fontWeight: 500 }}>✨ AI suggested</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removePart(index)}
                      aria-label="Remove part"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>

                  <div className="form-field">
                    <label className="field-label">Part Name (Optional)</label>
                    <input
                      type="text"
                      className={`field-input ${isFilled(part.name) ? "field-input--filled" : ""}`}
                      value={part.name || ""}
                      placeholder="e.g., Shell, Handle, Cap"
                      onChange={(e) =>
                        updateArrayData("materialAcquisition", "parts", index, "name", e.target.value)
                      }
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label className="field-label">
                        Material Category
                        <span className="field-required">*</span>
                      </label>
                      <select
                        className={`field-input field-select ${getPartError(index, "category") ? "field-input--error" : ""} ${isFilled(part.material_category) ? "field-input--filled" : ""}`}
                        value={part.material_category || ""}
                        onChange={(e) => handlePartCategoryChange(index, e.target.value)}
                      >
                        <option value="">Select category...</option>
                        {materialCategories.map((cat) => (
                          <option key={cat.category} value={cat.category}>{cat.category}</option>
                        ))}
                      </select>
                      {getPartError(index, "category") && (
                        <div className="field-error">Category is required</div>
                      )}
                    </div>

                    <div className="form-field">
                      <label className="field-label">
                        Material Subcategory
                        <span className="field-required">*</span>
                      </label>
                      <select
                        className={`field-input field-select ${getPartError(index, "subcategory") ? "field-input--error" : ""} ${isFilled(part.material_subcategory) ? "field-input--filled" : ""}`}
                        value={part.material_subcategory || ""}
                        onChange={(e) => handlePartSubcategoryChange(index, e.target.value)}
                        disabled={!part.material_category}
                        style={!part.material_category ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                      >
                        <option value="">Select subcategory...</option>
                        {categorySubcategories.map((sub) => (
                          <option key={sub.subcategory} value={sub.subcategory}>{sub.subcategory}</option>
                        ))}
                      </select>
                      {getPartError(index, "subcategory") && (
                        <div className="field-error">Subcategory is required</div>
                      )}
                    </div>
                  </div>

                  {availableQualities.length > 0 && (
                    <div className="form-field">
                      <label className="field-label">
                        Quality
                        <span className="field-required">*</span>
                      </label>
                      <select
                        className={`field-input field-select ${getPartError(index, "quality") ? "field-input--error" : ""} ${isFilled(part.material_quality) ? "field-input--filled" : ""}`}
                        value={part.material_quality || ""}
                        onChange={(e) => handlePartQualityChange(index, e.target.value)}
                        disabled={!part.material_subcategory}
                        style={!part.material_subcategory ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                      >
                        <option value="">Select quality...</option>
                        {availableQualities.map((qual) => (
                          <option key={qual.quality} value={qual.quality}>{qual.quality}</option>
                        ))}
                      </select>
                      {getPartError(index, "quality") && (
                        <div className="field-error">Quality is required</div>
                      )}
                    </div>
                  )}

                  {/* Process Category — only shown when the API returns at least one
                      process category. Hidden for per-item materials: process EFs are
                      per kg and a per-item part has no weight, so the selection would
                      silently contribute nothing. */}
                  {part.material_subcategory && unitForPart(part) !== 'per_item' && (() => {
                    const procCatKey = `${part.material_category}::${part.material_subcategory}`;
                    const availableProcCats = processCategoriesCache[procCatKey] || [];
                    if (availableProcCats.length === 0) return null;
                    return (
                      <div className="form-row">
                        <div className="form-field">
                          <label className="field-label">
                            Process Category
                            <span className="field-optional"> (if applicable)</span>
                          </label>
                          <select
                            className={`field-input field-select ${isFilled(part.process_category_id) ? "field-input--filled" : ""}`}
                            value={part.process_category_id || ""}
                            onChange={(e) => handlePartProcessCategoryChange(index, e.target.value)}
                          >
                            <option value="">Select process category...</option>
                            {availableProcCats.map((cat) => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>

                        <div className="form-field">
                          <label className="field-label">
                            Process
                            <span className="field-optional"> (if applicable)</span>
                          </label>
                          <select
                            className={`field-input field-select ${isFilled(part.process_id) ? "field-input--filled" : ""}`}
                            value={part.process_id || ""}
                            onChange={(e) => handlePartProcessChange(index, e.target.value)}
                            disabled={!part.process_category_id}
                            style={!part.process_category_id ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                          >
                            <option value="">Select process...</option>
                            {(processesCache[`${part.material_category}::${part.material_subcategory}::${part.process_category_id}`] || []).map((proc) => (
                              <option key={proc.id} value={proc.id}>{proc.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Per-item materials are counted per unit, so quantity fully
                      describes them and no weight is asked for. Everything else
                      is weighed as before. */}
                  {unitForPart(part) === 'per_item' ? (
                    <div className="form-field">
                      <label className="field-label">
                        Quantity
                        <span className="field-required">*</span>
                        <ZilchTooltip copy={"How many of these go into one finished product. This material's footprint is measured per item rather than by weight, so no weight is needed."} />
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className={`field-input ${getPartError(index, "quantity") ? "field-input--error" : ""} ${isFilled(part.quantity) ? "field-input--filled" : ""}`}
                        value={part.quantity ?? ""}
                        placeholder="e.g., 1"
                        onChange={(e) => {
                          const quantity = e.target.value;
                          updateArrayData("materialAcquisition", "parts", index, "quantity", quantity);
                          recalcPart(index, part, { quantity });
                        }}
                      />
                      {getPartError(index, "quantity") && (
                        <div className="field-error">Quantity is required</div>
                      )}
                      <div className="field-hint">
                        Counted per item — no weight needed.
                      </div>
                    </div>
                  ) : (
                    <div className="form-field">
                      <label className="field-label">
                        Weight (grams)
                        <span className="field-required">*</span>
                      </label>
                      <input
                        type="number"
                        className={`field-input ${getPartError(index, "weight") ? "field-input--error" : ""} ${isFilled(part.weight_grams) ? "field-input--filled" : ""}`}
                        value={part.weight_grams || ""}
                        placeholder="e.g., 50"
                        onChange={(e) => {
                          const weight = e.target.value;
                          updateArrayData("materialAcquisition", "parts", index, "weight_grams", weight);
                          recalcPart(index, part, { weight_grams: weight });
                        }}
                      />
                      {getPartError(index, "weight") && (
                        <div className="field-error">Weight is required</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <button
                type="button"
                className="btn-add "
                onClick={addPart}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add Part
              </button>

            {/* Weight Validation Display */}
            {formData.materialAcquisition.parts.length > 0 && (() => {
              // With a per-item part in the mix the weighed parts legitimately
              // fall short of the product weight, so the bar tracks "no more
              // than" rather than "equal to".
              const relaxed = hasPerItemPart();
              const over = isOverProductWeight();

              // Don't judge the total until every weighed part actually has a
              // weight. Before that the running total is low simply because the
              // user hasn't finished typing, and calling that a mismatch is
              // noise — the blocking check still runs on Next either way.
              const awaitingWeights = formData.materialAcquisition.parts.some(
                (part) => unitForPart(part) !== 'per_item' && !(Number(part.weight_grams) > 0)
              );

              const ok = relaxed ? !over : isWeightWithinTolerance();
              const barColor = over
                ? '#f59e0b'
                : awaitingWeights
                  ? '#94a3b8'
                  : ok ? '#10b981' : '#ef4444';

              return (
              <div className="weight-validation-section" style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#475569' }}>
                    {relaxed ? 'Weighed Parts:' : 'Total Parts Weight:'}
                  </span>
                  <span style={{ fontSize: '16px', fontWeight: '600', color: barColor }}>
                    {calculateTotalPartsWeight()}g / {formData.productInfo.weight || 0}g
                  </span>
                </div>

                {/* Progress bar */}
                <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      backgroundColor: barColor,
                      width: `${Math.min(100, (calculateTotalPartsWeight() / (formData.productInfo.weight || 1)) * 100)}%`,
                      transition: 'width 0.3s ease, background-color 0.3s ease'
                    }}
                  />
                </div>

                {/* Per-item parts present: no lower bound, but parts still
                    can't outweigh the finished product. */}
                {relaxed && over && (
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M10 6V10M10 14H10.01M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626', marginBottom: '4px' }}>
                          Parts weigh more than the product
                        </div>
                        <div style={{ fontSize: '13px', color: '#991b1b' }}>
                          Your weighed parts come to {calculateTotalPartsWeight()}g, which is {(calculateTotalPartsWeight() - Number(formData.productInfo.weight || 0)).toFixed(1)}g more than the {formData.productInfo.weight || 0}g product weight, and that's before the item-based parts.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {relaxed && !over && (
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                    Some parts are counted per item rather than by weight, so your parts don't need to add up to the product weight.
                  </div>
                )}

                {/* Still being filled in — prompt rather than scold. */}
                {!relaxed && awaitingWeights && (
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                    Add each part's weight, together they should come to about {formData.productInfo.weight || 0}g
                    {Number(formData.productInfo.weight) > 0 && ` (${(formData.productInfo.weight * 0.95).toFixed(1)}g - ${(formData.productInfo.weight * 1.05).toFixed(1)}g)`}.
                  </div>
                )}

                {/* Tolerance message */}
                {!relaxed && !awaitingWeights && !isWeightWithinTolerance() && (
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M10 6V10M10 14H10.01M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626', marginBottom: '4px' }}>
                          Weight Mismatch
                        </div>
                        <div style={{ fontSize: '13px', color: '#991b1b' }}>
                          Total parts weight must be within ±5% of product weight ({(formData.productInfo.weight * 0.95).toFixed(1)}g - {(formData.productInfo.weight * 1.05).toFixed(1)}g). 
                          Please adjust your parts to match.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {!relaxed && !awaitingWeights && isWeightWithinTolerance() && calculateTotalPartsWeight() > 0 && (
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="#10b981"/>
                    </svg>
                    <span style={{ fontSize: '13px', color: '#166534', fontWeight: '500' }}>
                      Weight validation passed
                    </span>
                  </div>
                )}
              </div>
              );
            })()}
          </div>
        )}

        {/* Divider */}
        <div className="form-divider"></div>

        {/* Decoration/Branding Processes */}
        <div className="form-field">
          <div className="section-header">
            <div>
              <h3 className="section-title">Decoration & Branding</h3>
              <p className="section-description">Add any printing, engraving, or branding processes (optional)</p>
            </div>
            <button
              type="button"
              className="btn-add"
              onClick={addBrandingProcess}
              disabled={decorationOptionsLoading}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add Process
            </button>
          </div>

          {decorationOptionsLoading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading decoration options...</p>
            </div>
          )}

          {decorationOptionsError && (
            <div className="alert-warning">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 6V10M10 14H10.01M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {decorationOptionsError}
            </div>
          )}

          {(!formData.decorationProcesses || formData.decorationProcesses.length === 0) && !decorationOptionsLoading && (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M16 12H32M14 20H34M12 28H36M10 36H38" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p className="empty-state-text">No decoration processes added yet.</p>
            </div>
          )}

          {(formData.decorationProcesses || []).map((proc, pIndex) => (
            <div key={pIndex} className="item-card">
              <div className="item-card-header">
                <span className="item-badge">Process {pIndex + 1}</span>
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => removeBrandingProcess(pIndex)}
                  aria-label="Remove process"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              <div className="form-row">
                <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="field-label">Decoration Type</label>
                  <select
                    className={`field-input field-select ${isFilled(proc.value) ? "field-input--filled" : ""}`}
                    value={proc.value || ""}
                    onChange={(e) =>
                      updateBrandingProcessField(pIndex, "value", e.target.value)
                    }
                  >
                    <option value="">Select decoration process...</option>
                    {decorationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-field">
                <label className="field-label">Quantity</label>
                <input
                  type="number"
                  className={`field-input ${isFilled(proc.quantity) ? "field-input--filled" : ""}`}
                  min="1"
                  step="1"
                  placeholder="How many times applied?"
                  value={proc.quantity || 1}
                  onChange={(e) =>
                    updateBrandingProcessField(pIndex, "quantity", parseInt(e.target.value) || 1)
                  }
                />
                <div className="field-hint">Number of times this process is applied</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Step2MaterialAcquisition;
